import InfoIcon from '@mui/icons-material/Info';
import { Paper, Grid, Typography, Tooltip, styled, Box, Avatar } from '@mui/material';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { MailIconAlt, WSIconTickMark } from '..';
import WsBadge from '../../components/WsBadge/WsBadge';
import { WsButton } from '../../components/WsButton';
import { WsElementWithPopOver } from '../../components/WsElementWithPopOver';
import CallIcon from '../../components/WsIcon/CallIcon';
import Ellipse from '../../components/WsIcon/Ellipse';
import PlusCircle from '../../components/WsIcon/PlusCircle';
import WsIconCopy from '../../components/WsIcon/WsIconCopy';
import { WsMoreOptionButton } from '../../components/WsMoreOptionButton';
import { WsTypography } from '../../components/WsTypography';
import { copyStringToClipboard } from '../../helper/copyStringToClipboard';
import { useNotificationStore } from '../../helper/NotificationStore';
import { success } from '../../helper/NotificationStore/NotificationMessages';
import { getSocialIconByURL } from '../../helper/SocialIcons';
import IWsPageHeader from '../../types/IWsPageHeader';
import WsColors from '../../WsColors';
import { WsBadgeSize, WsBadgetype, WsColorsNew, WsFontSize } from '../../WsEnums';
import { WsSelect } from '../WsSelect';

const StyledPaper = styled(Paper)<{ hasSubtitle: boolean; whiteBackground: boolean }>(
  ({ whiteBackground, theme }) => ({
    borderRadius: 10,
    padding: theme.spacing(1.5, 2),
    backgroundColor: whiteBackground ? 'transparent' : WsColors.White,
    height: 'auto',
    width: 'auto',
    boxShadow: whiteBackground
      ? 'none'
      : `${theme.spacing(0)} ${theme.spacing(0.125)} ${theme.spacing(0.375)} rgba(0, 0, 0, 0.12)`,
  })
);

const Img = styled('img')(() => ({
  height: 25,
  width: 25,
  cursor: 'pointer',
  marginRight: 7,
}));
const WsPageHeader = ({
  title,
  tooltip,
  subtitle,
  dropDownOptions = [],
  onDropdownClick,
  onButtonClick,
  buttonTitle,
  badgeLable,
  hyperLink,
  text,
  onMoreOptionClick,
  socialUrl,
  phoneNumbers = [],
  emails = [],
  profileImg,
  pendingQueue,
  moreOptions = [],
  transparent = false,
  rightChildren,
}: IWsPageHeader) => {
  const methods = useForm();
  const visibleElements: React.ReactNode[] = [];
  const setNotification = useNotificationStore((state) => state.setNotification);

  const copy = (value: string) => {
    copyStringToClipboard(value);
    setNotification(success('Copied Successfully !!'));
  };

  const openInNewPage = (path: string) => window.open(path ?? '', '_blank');

  if (pendingQueue) {
    visibleElements.push(
      <Grid item key="badge">
        <WsBadge
          label={`Pending queue - ${pendingQueue}`}
          badgeType={WsBadgetype.FAILED}
          badgeSize={WsBadgeSize.LARGE}
        />
      </Grid>
    );
  }

  if (badgeLable) {
    visibleElements.push(
      <Grid item key="badge">
        <WsBadge
          icon={<WSIconTickMark />}
          startIcon={true}
          label={badgeLable ?? ''}
          badgeType={WsBadgetype.SUCCESS}
          badgeSize={WsBadgeSize.LARGE}
        />
      </Grid>
    );
  }

  if (hyperLink?.buttonName && hyperLink.path) {
    visibleElements.push(
      <Grid item key="lead">
        <WsButton variant="text" onClick={() => openInNewPage(hyperLink.path)}>
          {hyperLink.buttonName ?? ''}
        </WsButton>
      </Grid>
    );
  }

  if (socialUrl) {
    visibleElements.push(
      <Grid item key="linkedin">
        <WsElementWithPopOver
          element={
            <Img src={getSocialIconByURL(socialUrl ?? 'https://www.linkedin.com/in/example')} />
          }
        >
          <Grid container direction="column" p={1.5}>
            <Grid item>
              <WsButton
                startIcon={<WsIconCopy color={WsColors.TertiaryShades800} />}
                onClick={() => openInNewPage(socialUrl)}
                size="small"
                variant="text"
              >
                Open In a New Tab
              </WsButton>
            </Grid>
            <Grid item>
              <WsButton
                startIcon={<WsIconCopy />}
                onClick={() => copy(socialUrl)}
                variant="text"
                size="small"
              >
                Copy Link
              </WsButton>
            </Grid>
          </Grid>
        </WsElementWithPopOver>
      </Grid>
    );
  }

  if (text) {
    visibleElements.push(
      <Grid item key="text">
        <WsTypography>{text}</WsTypography>
      </Grid>
    );
  }
  if (phoneNumbers?.[0]) {
    visibleElements.push(
      <Grid item key="contact">
        <WsBadge
          icon={<CallIcon />}
          startIcon={true}
          menuList={true}
          label={phoneNumbers ? phoneNumbers[0] : 'NA'}
          badgeType={WsBadgetype.INACTIVE}
          menuOptions={phoneNumbers}
          badgeSize={WsBadgeSize.LARGE}
        />
      </Grid>
    );
  }

  if (emails?.[0]) {
    visibleElements.push(
      <Grid item key="contact">
        <WsBadge
          icon={<MailIconAlt />}
          startIcon={true}
          menuList={true}
          label={emails ? emails[0] : 'NA'}
          badgeType={WsBadgetype.INACTIVE}
          menuOptions={emails}
          badgeSize={WsBadgeSize.LARGE}
        />
      </Grid>
    );
  }

  const itemsWithEllipses = visibleElements.flatMap((item, index) => {
    if (index === visibleElements.length - 1) return [item];
    return [
      item,
      <Box key={`ellipse-${index}`} mt={0.5} ml={0.5}>
        <Ellipse color={WsColors.TertiaryShades600} />
      </Box>,
    ];
  });

  return (
    <FormProvider {...methods}>
      <StyledPaper elevation={1} hasSubtitle={!!subtitle} whiteBackground={transparent}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Grid container alignItems="center" spacing={1}>
              {profileImg?.required && (
                <Grid item>
                  <Avatar
                    variant="square"
                    alt={title}
                    src={profileImg.profileImage}
                    sx={{ width: 40, height: 40, borderRadius: 1 }}
                  />
                </Grid>
              )}
              {title && (
                <Grid item>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ color: WsColorsNew.neutral_950, fontSize: 20 }}
                  >
                    {title}
                  </Typography>
                </Grid>
              )}
              {dropDownOptions?.length > 0 && (
                <Grid item>
                  <WsSelect
                    name="menu"
                    selectArray={dropDownOptions}
                    defaultValue={dropDownOptions[0].value.toString() ?? ''}
                    onChangeValue={onDropdownClick}
                  />
                </Grid>
              )}
              {tooltip && (
                <Grid item marginTop={0.8}>
                  <Tooltip title={tooltip}>
                    <InfoIcon sx={{ fontSize: 16, color: WsColorsNew.neutral_600 }} />
                  </Tooltip>
                </Grid>
              )}
              {itemsWithEllipses}
            </Grid>
          </Grid>
          <Grid item>
            <Grid container alignItems="center" spacing={1}>
              {buttonTitle && (
                <Grid item>
                  <WsButton
                    variant="contained"
                    size="medium"
                    onClick={onButtonClick}
                    startIcon={<PlusCircle color={WsColors.White} />}
                  >
                    {buttonTitle ?? ''}
                  </WsButton>
                </Grid>
              )}
              {rightChildren && <Grid item>{rightChildren}</Grid>}
              {moreOptions?.length > 0 && (
                <Grid item>
                  <WsMoreOptionButton
                    onItemClick={onMoreOptionClick ?? (() => {})}
                    items={moreOptions}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        {subtitle && (
          <Grid container>
            <Grid item sx={{ pl: profileImg ? 6 : 0 }}>
              <Typography
                variant="body2"
                sx={{ color: WsColorsNew.neutral_700, mt: 1, fontSize: WsFontSize.BODY_S }}
              >
                {subtitle}
              </Typography>
            </Grid>
          </Grid>
        )}
      </StyledPaper>
    </FormProvider>
  );
};

export default WsPageHeader;
